import type { TravelQuery } from "@goblin/domain";
import { useEffect, useState } from "react";
import { type SearchEvent, streamSearch } from "../../../api/client";
import { OfferCard } from "./OfferCard";

type Stage = {
  name: string;
  elapsedMs?: number;
  candidatesOut?: number;
};

export function ResultsStream({
  query,
  useRealPrices,
}: {
  query: TravelQuery;
  useRealPrices: boolean;
}) {
  const [offers, setOffers] = useState<unknown[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setOffers([]);
    setStages([]);
    setDone(false);
    setError(null);

    (async () => {
      try {
        for await (const ev of streamSearch(query, {
          flightProvider: useRealPrices ? "serpapi" : undefined,
        })) {
          if (cancelled) return;
          apply(ev);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();

    function apply(ev: SearchEvent) {
      switch (ev.type) {
        case "stage_started":
          setStages((s) => [...s, { name: ev.stage }]);
          break;
        case "stage_completed":
          setStages((s) =>
            s.map((st, i, arr) =>
              i === arr.length - 1
                ? { ...st, elapsedMs: ev.elapsedMs, candidatesOut: ev.candidatesOut }
                : st,
            ),
          );
          break;
        case "offer":
          setOffers((o) => [...o, ev.offer]);
          break;
        case "error":
          setError(ev.message);
          break;
        case "done":
          setDone(true);
          break;
      }
    }

    return () => {
      cancelled = true;
    };
  }, [query, useRealPrices]);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-300">Pipeline</h3>
        <ol className="space-y-1 text-sm" aria-live="polite">
          {stages.map((s) => (
            <li key={s.name} className="flex justify-between">
              <span className="font-mono text-slate-600 dark:text-slate-300">{s.name}</span>
              <span className="tabular-nums text-slate-500 dark:text-slate-400">
                {s.candidatesOut !== undefined ? `${s.candidatesOut} candidates · ` : ""}
                {s.elapsedMs !== undefined ? `${s.elapsedMs}ms` : "…"}
              </span>
            </li>
          ))}
        </ol>
        {error && (
          <p
            className="mt-2 rounded-md bg-red-50 p-2 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300"
            role="alert"
          >
            {error}
          </p>
        )}
        {done && !error && (
          <p className="mt-2 text-sm text-goblin-700 dark:text-goblin-500">
            Listo. {offers.length} oferta{offers.length === 1 ? "" : "s"}.
          </p>
        )}
      </div>

      <div className="space-y-2">
        {offers.map((o, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: offers arrive via SSE append-only
          <OfferCard key={`offer-${i}`} offer={o} />
        ))}
        {offers.length === 0 && !done && !error && (
          <p className="text-sm italic text-slate-500 dark:text-slate-400">Buscando…</p>
        )}
        {offers.length === 0 && done && !error && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Sin ofertas. Probá relajar los trade-offs o ampliar el rango de fechas.
          </p>
        )}
      </div>
    </div>
  );
}
